import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { onboardingApi } from '../api/onboarding';
import type { OnboardingStatus } from '../types/api';

interface UserContextType {
    status: OnboardingStatus | null;
    isLoading: boolean;
    error: string | null;
    refreshStatus: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const [status, setStatus] = useState<OnboardingStatus | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const fetchStatus = async () => {
        if (!isAuthenticated) {
            setStatus(null);
            return;
        }

        // Prevent duplicate parallel fetches
        if (isLoading) return;

        setIsLoading(true);
        try {
            const data = await onboardingApi.getStatus();
            // Only update if data actually changed (deep comparison would be better, but simple ref check helps)
            setStatus(prev => {
                if (JSON.stringify(prev) === JSON.stringify(data)) return prev;
                return data;
            });
            setError(null);
        } catch (err: any) {
            console.error('Failed to fetch user status', err);
            setError(err.message || 'Failed to load user profile');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isAuthenticated && !status && !isLoading && !error) {
            fetchStatus();
        }
    }, [isAuthenticated]);

    return (
        <UserContext.Provider value={{ status, isLoading, error, refreshStatus: fetchStatus }}>
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};
